type Listener = (...args: any[]) => void;

/**
 * Minimal browser-safe EventEmitter replacement for the subset ui-common uses.
 *
 * This avoids importing Node's `events` module in the ESM/browser build while
 * preserving the small API surface used by AuthHandler and ActivityMonitor.
 */
class EventEmitter {
  private listeners: Record<string, Listener[]> = {};

  /**
   * Registers a listener for the event name.
   */
  on(eventName: string, listener: Listener) {
    this.listeners[eventName] = [
      ...(this.listeners[eventName] ?? []),
      listener
    ];
    return this;
  }

  /**
   * Removes a previously registered listener for the event name.
   */
  off(eventName: string, listener: Listener) {
    this.listeners[eventName] = (this.listeners[eventName] ?? []).filter(
      candidate => candidate !== listener
    );
    return this;
  }

  /**
   * Node EventEmitter compatibility alias for off.
   */
  removeListener(eventName: string, listener: Listener) {
    return this.off(eventName, listener);
  }

  /**
   * Emits an event to registered listeners.
   *
   * Matches Node EventEmitter's return contract: true when at least one
   * listener was called, otherwise false.
   */
  emit(eventName: string, ...args: any[]) {
    const eventListeners = this.listeners[eventName] ?? [];
    for (const listener of eventListeners) {
      listener(...args);
    }
    return eventListeners.length > 0;
  }
}

export { EventEmitter };
